/**
 * InvestigationBar — the smart investigation back button, the clickable
 * Investigation Path, the private "Current Investigation" panel, and the
 * Bookmark / Share investigation actions (V2.0 Phase A + B).
 * Quiet Archive styling; renders nothing when there is nothing to show.
 */
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CornerLeftUp, ChevronDown, ChevronUp, Bookmark, Share2,
} from "lucide-react";
import { useMemberAuth } from "../hooks/useMemberAuth";
import { useScrollIdle } from "../hooks/useScrollIdle";
import { usePastHeader } from "../hooks/usePastHeader";
import * as memory from "./investigationMemory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CRUMBS = 4;

const fmtClock = (ts) =>
  new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const fmtElapsed = (ms) => {
  const m = Math.max(Math.floor(ms / 60000), 0);
  if (m < 1) return "just started";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const contentSteps = (stack, currentUrl, currentTitle) => {
  const all = [...stack.map((e) => ({ url: e.url, title: e.title })),
               { url: currentUrl, title: currentTitle }];
  return all.filter((s) => /^\/(topic|people|organizations|operations)\//.test(s.url.split("?")[0]));
};

const JStat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="font-display text-[17px] text-[#E4DFD3] leading-none">{value}</div>
    <div className="font-mono text-[8px] uppercase tracking-[0.28em] text-[#7B7168] mt-1">{label}</div>
  </div>
);

export const InvestigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useMemberAuth();
  const scrolling = useScrollIdle();
  const pastHeader = usePastHeader();
  useSyncExternalStore(memory.subscribe, memory.getVersion);

  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, [open]);

  const stack = memory.getStack();
  const resume = memory.getResumeOffer();
  if (!memory.isArchivePath(location.pathname)) return null;
  if (stack.length === 0 && !resume) return null;

  const currentUrl = location.pathname + location.search;
  const currentTitle = memory.getCurrentTitle() || memory.titleFor(currentUrl);
  const journey = memory.getJourney();

  const goBack = () => {
    const e = memory.goBackEntry();
    if (e) navigate(e.url);
  };
  const jump = (i) => {
    const e = memory.jumpToEntry(i);
    if (e) navigate(e.url);
  };
  const doResume = () => {
    memory.restoreScroll(resume.scroll);
    memory.clearResumeOffer();
  };

  const doBookmark = async () => {
    try {
      await axios.post(`${API}/members/me/investigations`, {
        title: currentTitle,
        current_url: location.pathname,
        scroll: Math.round(window.scrollY),
        path: contentSteps(stack, currentUrl, currentTitle),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Investigation bookmarked — continue it any time from your Member Desk.");
    } catch {
      toast.error("Could not bookmark this investigation.");
    }
  };

  const doShare = async () => {
    const steps = contentSteps(stack, currentUrl, currentTitle);
    if (steps.length < 2) {
      toast("Follow at least one connection first — a trail needs two stops.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/trails`, {
        path: steps,
        evidence_viewed: journey.evidence,
        sources_opened: journey.sources,
      });
      const link = `${window.location.origin}/api/t/${data.id}`;
      try { await navigator.clipboard.writeText(link); } catch { /* fallback below */ }
      toast.success("Investigation link copied to clipboard.", { description: link });
    } catch {
      toast.error("Could not create the investigation link.");
    }
  };

  const tail = stack.slice(-MAX_CRUMBS);
  const offset = stack.length - tail.length;

  return (
    <>
      <div
        data-testid="investigation-bar"
        className="border-b"
        style={{ borderColor: "#221F1B", background: "rgba(17,15,13,0.94)" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
          {stack.length > 0 && (
            <button
              type="button"
              onClick={goBack}
              data-testid="investigation-back-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase
                         tracking-[0.28em] text-[#A68F5E] hover:text-[#D92D20]
                         transition-colors shrink-0 py-0.5"
            >
              <ArrowLeft size={11} /> Return to Previous Investigation
            </button>
          )}

          {stack.length > 0 && (
            <div
              data-testid="investigation-path"
              className="flex items-center gap-1.5 flex-wrap min-w-0"
            >
              <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#5A554C] shrink-0">
                Investigation Path
              </span>
              {offset > 0 && (
                <span className="font-mono text-[9px] text-[#5A554C]">…</span>
              )}
              {tail.map((e, i) => (
                <React.Fragment key={`${e.url}-${offset + i}`}>
                  <button
                    type="button"
                    onClick={() => jump(offset + i)}
                    data-testid={`investigation-path-item-${offset + i}`}
                    title={e.title}
                    className="font-mono text-[9.5px] text-[#8A8377] hover:text-[#D92D20]
                               transition-colors underline underline-offset-2
                               decoration-[#3A3630] max-w-[16ch] truncate"
                  >
                    {e.title}
                  </button>
                  <span className="text-[#3A3630] text-[9px]">→</span>
                </React.Fragment>
              ))}
              <span
                className="font-mono text-[9.5px] text-[#E4DFD3] max-w-[20ch] truncate"
                data-testid="investigation-path-current"
                title={currentTitle}
              >
                {currentTitle}
              </span>
            </div>
          )}

          {resume && (
            <button
              type="button"
              onClick={doResume}
              data-testid="resume-investigation-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase
                         tracking-[0.28em] text-[#E4DFD3] border px-2.5 py-1
                         hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
              style={{ borderColor: "#3A3630" }}
            >
              <CornerLeftUp size={10} /> Resume Investigation
            </button>
          )}

          {/* Right rail: journey / bookmark / share */}
          <div className="ml-auto flex items-center gap-3">
            {token && (
              <button
                type="button"
                onClick={doBookmark}
                data-testid="bookmark-investigation-btn"
                title="Bookmark Investigation"
                className="inline-flex items-center gap-1 font-mono text-[8.5px] uppercase
                           tracking-[0.22em] text-[#7B7168] hover:text-[#A68F5E] transition-colors"
              >
                <Bookmark size={11} /> <span className="hidden sm:inline">Bookmark</span>
              </button>
            )}
            <button
              type="button"
              onClick={doShare}
              data-testid="share-investigation-btn"
              title="Share Investigation"
              className="inline-flex items-center gap-1 font-mono text-[8.5px] uppercase
                         tracking-[0.22em] text-[#7B7168] hover:text-[#A68F5E] transition-colors"
            >
              <Share2 size={11} /> <span className="hidden sm:inline">Share</span>
            </button>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="current-investigation-toggle"
              className="inline-flex items-center gap-1 font-mono text-[8.5px] uppercase
                         tracking-[0.22em] text-[#7B7168] hover:text-[#E4DFD3] transition-colors"
            >
              Current Investigation {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="current-investigation-panel"
            className="border-t"
            style={{ borderColor: "#221F1B", background: "rgba(13,12,10,0.9)" }}
          >
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 grid grid-cols-3 md:grid-cols-6 gap-4"
                 data-tick={tick}>
              <JStat label="Started" value={fmtClock(journey.startedAt)} testid="ci-started" />
              <JStat label="Time investigating" value={fmtElapsed(Date.now() - journey.startedAt)} testid="ci-time" />
              <JStat label="Pages explored" value={journey.pages} testid="ci-pages" />
              <JStat label="Connections followed" value={journey.connections} testid="ci-connections" />
              <JStat label="Evidence viewed" value={journey.evidence} testid="ci-evidence" />
              <JStat label="Primary sources opened" value={journey.sources} testid="ci-sources" />
            </div>
          </div>
        )}
      </div>

      {/* Mobile: thumb-reachable back control */}
      {stack.length > 0 && (
        <button
          type="button"
          onClick={goBack}
          data-testid="investigation-back-mobile"
          className={"md:hidden fixed left-4 z-40 inline-flex items-center gap-1.5 " +
                     "font-mono text-[9px] uppercase tracking-[0.22em] text-[#E4DFD3] " +
                     "border px-3 py-2 backdrop-blur-sm active:text-[#D92D20] " +
                     "transition-opacity duration-200 " +
                     (scrolling || !pastHeader ? "opacity-0 pointer-events-none" : "opacity-100")}
          style={{
            borderColor: "#3A3630",
            background: "rgba(17,15,13,0.9)",
            bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <ArrowLeft size={11} /> Previous
        </button>
      )}
    </>
  );
};
